/**
 * Accessory product page: gallery thumbnails, option buttons and the quantity
 * stepper. A single-option form works without this (size radios are
 * `required`). With it, Add to bag stays disabled until every option is
 * picked, and the price and ?variant= follow the choice. With several options,
 * this picks the variant from the chosen values ([data-variants]), sets the
 * form's id and strikes through values that can't be bought with the others.
 */
class ProductPage extends HTMLElement {
  connectedCallback() {
    this.addButton = this.querySelector('[data-add]');
    this.hint = this.querySelector('[data-size-hint]');
    this.price = this.querySelector('[data-price]');
    this.quantity = this.querySelector('input[name="quantity"]');
    this.variantId = this.querySelector('[data-variant-id]');
    const variantsData = this.querySelector('[data-variants]');
    this.variants = variantsData ? JSON.parse(variantsData.textContent) : null;
    this.optionGroups = [...this.querySelectorAll('[data-option]')];

    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-show-media]');
      if (thumb) this.showMedia(thumb);
      const step = event.target.closest('[data-step]');
      if (step) this.stepQuantity(Number(step.dataset.step));
      const guideLink = event.target.closest('[data-open-guide]');
      if (guideLink) document.getElementById(guideLink.dataset.openGuide)?.setAttribute('open', '');
    });
    this.addEventListener('change', (event) => {
      if (event.target.name === 'id') this.onVariantChange(event.target);
      if (event.target.matches('[data-option-value]')) this.onOptionChange();
    });
    if (this.variants) this.onOptionChange();
    else this.updateAddButton();
  }

  /** Several options: the chosen value of each, or null where none is chosen yet. */
  chosenOptions() {
    return this.optionGroups.map((group) => group.querySelector('[data-option-value]:checked')?.value ?? null);
  }

  onOptionChange() {
    const chosen = this.chosenOptions();
    // A value is struck through when no available variant has it with the other chosen values.
    this.optionGroups.forEach((group, index) => {
      group.querySelectorAll('[data-option-value]').forEach((input) => {
        const buyable = this.variants.some((variant) => variant.available && variant.options.every(
          (value, i) => (i === index ? value === input.value : chosen[i] === null || chosen[i] === value)
        ));
        input.closest('.size').classList.toggle('size--unavailable', !buyable);
      });
    });

    const variant = chosen.includes(null)
      ? null
      : this.variants.find((v) => v.options.every((value, i) => value === chosen[i]));
    if (this.variantId) {
      this.variantId.value = variant ? variant.id : '';
      this.variantId.disabled = !variant;
    }
    if (variant) {
      if (this.price) this.price.textContent = variant.price;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    this.updateAddButton(variant);
  }

  showMedia(thumb) {
    this.querySelectorAll('[data-media]').forEach((media) => {
      media.hidden = media.dataset.media !== thumb.dataset.showMedia;
    });
    this.querySelectorAll('[data-show-media]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === thumb));
    });
  }

  stepQuantity(delta) {
    const min = Number(this.quantity.min) || 1;
    this.quantity.value = Math.max(min, (Number(this.quantity.value) || min) + delta);
  }

  onVariantChange(input) {
    const source = input.tagName === 'SELECT' ? input.selectedOptions[0] : input;
    if (this.price && source?.dataset.price) this.price.textContent = source.dataset.price;
    const chosenLabel = this.querySelector('[data-size-chosen]');
    const sizeLabel = input.closest('.size')?.querySelector('[data-size-label]');
    if (chosenLabel && sizeLabel) chosenLabel.textContent = sizeLabel.textContent;
    this.updateAddButton();

    const url = new URL(window.location.href);
    url.searchParams.set('variant', input.value);
    window.history.replaceState({}, '', url);
  }

  /** `variant` is the chosen variant with several options (null until all are chosen). */
  updateAddButton(variant) {
    if (!this.addButton?.hasAttribute('data-needs-size')) return;
    const chosen = this.variants ? Boolean(variant) : Boolean(this.querySelector('input[name="id"]:checked'));
    const soldOut = Boolean(variant) && !variant.available;
    this.addButton.disabled = !chosen || soldOut;
    const { soldOutLabel, addLabel, chooseLabel } = this.addButton.dataset;
    this.addButton.textContent = soldOut ? soldOutLabel : (!chosen && chooseLabel) || addLabel;
    if (this.hint) this.hint.hidden = chosen;
  }
}
customElements.define('product-page', ProductPage);

/**
 * Hero fabric crossfade: moves `is-active` to the next slide every
 * `data-interval` ms. Stays still under reduced motion, with fewer than two
 * slides, while the tab is hidden, and in the theme editor, where selecting a
 * fabric block shows that slide instead.
 */
class FabricSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.children];
    this.onBlockSelect = (event) => {
      const index = this.slides.indexOf(event.target);
      if (index !== -1) this.show(index);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || this.slides.length < 2 || window.Shopify?.designMode) return;

    const interval = Number(this.dataset.interval) || 5000;
    this.timer = setInterval(() => this.next(), interval);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  next() {
    if (document.hidden) return;
    const current = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    this.show((current + 1) % this.slides.length);
  }

  show(index) {
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
  }
}
customElements.define('fabric-slideshow', FabricSlideshow);

/**
 * Mobile menu drawer: a native modal <dialog>, which traps focus and closes on
 * Esc. Also closes on the ✕ button, a backdrop click, a link click, and when
 * the window widens past the mobile breakpoint.
 */
class MenuDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.openButton = this.querySelector('[data-menu-open]');
    if (!this.dialog || !this.openButton) return;

    this.openButton.addEventListener('click', () => {
      this.dialog.showModal();
      this.openButton.setAttribute('aria-expanded', 'true');
    });
    this.querySelector('[data-menu-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('a')) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => {
      this.openButton.setAttribute('aria-expanded', 'false');
      this.openButton.focus();
    });
    window.matchMedia('(min-width: 701px)').addEventListener('change', (event) => {
      if (event.matches && this.dialog.open) this.dialog.close();
    });
  }
}
customElements.define('menu-drawer', MenuDrawer);

/**
 * Tweed preview on a silhouette drawing: <garment-preview data-drawing="…"
 * data-tweed="…">fallback</garment-preview>. The drawing is a line drawing
 * (transparent or white background, closed outline). Its garment shape is found
 * by flooding the background in from the edges: whatever the outline encloses
 * is the garment. That shape masks the tweed, and the drawing's lines sit on
 * top. A drawing whose garment is filled (white) on a transparent background
 * uses its fill as the shape instead, so a closed-off background gap (under a
 * peplum jacket's arms) stays empty. Without a usable drawing (none,
 * cross-origin failure, or an outline with a gap that lets the flood in), the
 * fallback children stay: a plain
 * square of the tweed. With data-fit="content" the drawing is trimmed to the
 * garment (uploads are framed differently) and the element takes its shape,
 * so pieces can be set side by side as a set (giveaway).
 */
const garmentMasks = new Map();

function garmentMask(url) {
  if (!garmentMasks.has(url)) garmentMasks.set(url, buildGarmentMask(url).catch(() => null));
  return garmentMasks.get(url);
}

async function buildGarmentMask(url) {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.src = url;
  await image.decode();
  const scale = Math.min(1, 800 / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.round(image.naturalWidth * scale);
  const height = Math.round(image.naturalHeight * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  context.drawImage(image, 0, 0, width, height);
  const pixels = context.getImageData(0, 0, width, height);
  const data = pixels.data;
  const count = width * height;

  // Line pixels: visible and dark. Thickened by a pixel so a hairline gap left
  // by scaling doesn't let the flood through.
  const line = new Uint8Array(count);
  let left = width;
  let right = -1;
  let top = height;
  let bottom = -1;
  for (let i = 0; i < count; i += 1) {
    const lum = data[i * 4] * 0.3 + data[i * 4 + 1] * 0.59 + data[i * 4 + 2] * 0.11;
    line[i] = data[i * 4 + 3] > 24 && lum < 200 ? 1 : 0;
    if (line[i]) {
      const x = i % width;
      const y = (i - x) / width;
      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }
  const wall = line.slice();
  for (let i = 0; i < count; i += 1) {
    if (!line[i]) continue;
    const x = i % width;
    if (x > 0) wall[i - 1] = 1;
    if (x < width - 1) wall[i + 1] = 1;
    if (i >= width) wall[i - width] = 1;
    if (i < count - width) wall[i + width] = 1;
  }

  // Flood the background in from every edge pixel.
  const outside = new Uint8Array(count);
  const queue = new Int32Array(count);
  let head = 0;
  let tail = 0;
  const visit = (i) => {
    if (wall[i] || outside[i]) return;
    outside[i] = 1;
    queue[tail] = i;
    tail += 1;
  };
  for (let x = 0; x < width; x += 1) {
    visit(x);
    visit(count - width + x);
  }
  for (let y = 0; y < height; y += 1) {
    visit(y * width);
    visit(y * width + width - 1);
  }
  while (head < tail) {
    const i = queue[head];
    head += 1;
    const x = i % width;
    if (x > 0) visit(i - 1);
    if (x < width - 1) visit(i + 1);
    if (i >= width) visit(i - width);
    if (i < count - width) visit(i + width);
  }

  // Filled drawing: most of what the outline encloses is opaque (the garment is
  // filled, the background transparent). Then the background is every
  // transparent pixel, enclosed or not.
  let enclosed = 0;
  let opaque = 0;
  for (let i = 0; i < count; i += 1) {
    if (outside[i] || wall[i]) continue;
    enclosed += 1;
    if (data[i * 4 + 3] > 200) opaque += 1;
  }
  if (enclosed && opaque / enclosed > 0.6) {
    tail = 0;
    for (let i = 0; i < count; i += 1) {
      outside[i] = data[i * 4 + 3] <= 24 ? 1 : 0;
      if (outside[i]) {
        queue[tail] = i;
        tail += 1;
      }
    }
  }

  // Lines only (a filled drawing's white would cover the tweed).
  const linesCanvas = document.createElement('canvas');
  linesCanvas.width = width;
  linesCanvas.height = height;
  const linesContext = linesCanvas.getContext('2d');
  const linePixels = linesContext.createImageData(width, height);
  for (let i = 0; i < count; i += 1) {
    if (!line[i]) continue;
    linePixels.data[i * 4] = data[i * 4];
    linePixels.data[i * 4 + 1] = data[i * 4 + 1];
    linePixels.data[i * 4 + 2] = data[i * 4 + 2];
    linePixels.data[i * 4 + 3] = data[i * 4 + 3];
  }
  linesContext.putImageData(linePixels, 0, 0);

  // Pull the cloth in from the outer outline (owner, 2026-11-02), so a thin gap
  // keeps the drawing's outline visible: grow the background a few pixels inward.
  const inset = Math.max(2, Math.round(Math.max(width, height) * 0.011));
  let frontier = tail;
  head = 0;
  for (let step = 1; step <= inset; step += 1) {
    const end = frontier;
    while (head < end) {
      const i = queue[head];
      head += 1;
      const x = i % width;
      const grow = (j) => {
        if (outside[j]) return;
        outside[j] = 1;
        queue[tail] = j;
        tail += 1;
      };
      if (x > 0) grow(i - 1);
      if (x < width - 1) grow(i + 1);
      if (i >= width) grow(i - width);
      if (i < count - width) grow(i + width);
    }
    frontier = tail;
  }

  let inside = 0;
  for (let i = 0; i < count; i += 1) {
    const keep = outside[i] ? 0 : 255;
    data[i * 4] = 0;
    data[i * 4 + 1] = 0;
    data[i * 4 + 2] = 0;
    data[i * 4 + 3] = keep;
    if (keep) inside += 1;
  }
  // A gap in the outline floods the garment too (almost nothing left inside);
  // a drawing with no background floods nothing. Either way, no shape.
  if (inside < count * 0.04 || inside > count * 0.9) return null;

  // The garment's box (its lines, plus a hair of room), for data-fit="content".
  const pad = Math.round(Math.max(width, height) * 0.006);
  const box = {
    x: Math.max(0, left - pad),
    y: Math.max(0, top - pad),
  };
  box.w = Math.min(width, right + pad + 1) - box.x;
  box.h = Math.min(height, bottom + pad + 1) - box.y;
  const crop = (source) => {
    const out = document.createElement('canvas');
    out.width = box.w;
    out.height = box.h;
    out.getContext('2d').drawImage(source, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
    return out.toDataURL('image/png');
  };
  const trimmedLines = crop(linesCanvas);

  // How much of the trimmed drawing hangs below the garment at its centre (a
  // cropped jacket's sleeves below its hem), so a skirt can start at the hem.
  const centre = box.x + Math.floor(box.w / 2);
  let hem = box.y + box.h - 1;
  while (hem > box.y && outside[hem * width + centre]) hem -= 1;
  const belowHem = (box.y + box.h - 1 - hem) / box.h;

  context.putImageData(pixels, 0, 0);
  return {
    mask: canvas.toDataURL('image/png'),
    lines: linesCanvas.toDataURL('image/png'),
    trimmed: { mask: crop(canvas), lines: trimmedLines, aspect: box.w / box.h, belowHem },
  };
}

class GarmentPreview extends HTMLElement {
  static get observedAttributes() {
    return ['data-drawing', 'data-tweed'];
  }

  connectedCallback() {
    if (!this.fallback) this.fallback = [...this.childNodes];
    this.render();
  }

  attributeChangedCallback() {
    if (this.fallback) this.render();
  }

  async render() {
    const { drawing, tweed } = this.dataset;
    this.token = (this.token || 0) + 1;
    const token = this.token;
    const shape = drawing && tweed ? await garmentMask(drawing) : null;
    if (token !== this.token) return;
    if (!shape) {
      this.style.removeProperty('aspect-ratio');
      this.classList.remove('is-drawn');
      this.classList.add('is-fallback');
      if (this.fallback.some((node) => node.parentNode !== this)) this.replaceChildren(...this.fallback);
      return;
    }
    const fit = this.dataset.fit === 'content';
    const mask = fit ? shape.trimmed.mask : shape.mask;
    if (fit) {
      this.style.aspectRatio = String(shape.trimmed.aspect);
      this.style.setProperty('--garment-aspect', String(shape.trimmed.aspect));
      this.style.setProperty('--garment-below-hem', String(shape.trimmed.belowHem));
    }
    const cloth = document.createElement('div');
    cloth.className = 'garment-preview__cloth';
    cloth.style.backgroundImage = `url("${tweed}")`;
    cloth.style.setProperty('-webkit-mask-image', `url("${mask}")`);
    cloth.style.setProperty('mask-image', `url("${mask}")`);
    const lines = document.createElement('img');
    lines.className = 'garment-preview__lines';
    lines.alt = '';
    lines.src = fit ? shape.trimmed.lines : shape.lines;
    this.replaceChildren(cloth, lines);
    this.classList.remove('is-fallback');
    this.classList.add('is-drawn');
  }
}
customElements.define('garment-preview', GarmentPreview);

// Theme editor: selecting an FAQ question or a product accordion block opens it so its content is in view.
document.addEventListener('shopify:block:select', (event) => {
  if (event.target.matches('details.faq__item, details.accordion')) event.target.open = true;
});
document.addEventListener('shopify:block:deselect', (event) => {
  if (event.target.matches('details.faq__item, details.accordion')) event.target.open = false;
});

/**
 * No orphans: joins the last two words of headings and short text with a
 * non-breaking space, so a single word never sits alone on the last line.
 * `text-wrap: balance / pretty` in base.css handles most cases; this covers
 * three-word headings and browsers without text-wrap: pretty.
 */
function preventOrphans(root = document) {
  root.querySelectorAll('h1, h2, h3, p, li, dd, figcaption, summary').forEach((el) => {
    // Keep the brand name "R. YIN" on one line.
    const brandWalker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    for (let node = brandWalker.nextNode(); node; node = brandWalker.nextNode()) {
      if (node.data.includes('R. YIN')) node.data = node.data.replaceAll('R. YIN', 'R. YIN');
    }
    const words = el.textContent.trim().split(/\s+/).length;
    if (words < 3 || words > 40) return;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let last = null;
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (node.data.trim()) last = node;
    }
    if (!last) return;
    const trimmed = last.data.replace(/\s+$/, '');
    const space = trimmed.lastIndexOf(' ');
    if (space > 0) last.data = `${trimmed.slice(0, space)} ${trimmed.slice(space + 1)}${last.data.slice(trimmed.length)}`;
  });

  // Headings: keep hyphenated words whole ("MADE- / TO-MEASURE" reads badly). The
  // fonts have no non-breaking hyphen, so wrap each one in a no-wrap span.
  root.querySelectorAll('h1, h2, h3').forEach((heading) => {
    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
    const nodes = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) nodes.push(node);
    nodes.forEach((node) => {
      const match = node.data.match(/[^\s ]+-[^\s ]+/);
      if (!match || node.parentElement.classList.contains('nowrap')) return;
      const word = node.splitText(match.index);
      word.splitText(match[0].length);
      const span = document.createElement('span');
      span.className = 'nowrap';
      word.replaceWith(span);
      span.append(word);
    });
  });
}
document.addEventListener('shopify:section:load', (event) => preventOrphans(event.target));

/**
 * Swatch page: "Add swatch" adds to the bag without leaving the page, then
 * re-renders the header (Section Rendering API) so the bag count updates.
 * Without JavaScript the form posts normally.
 */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-swatch-form]');
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('[type="submit"]');
  const status = document.querySelector('[data-swatch-status]');
  const header = document.querySelector('.section-header');
  const headerId = header?.id.replace('shopify-section-', '');
  button.disabled = true;
  try {
    const body = new FormData(form);
    if (headerId) body.append('sections', headerId);
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || '');
    const html = data.sections?.[headerId];
    if (html) {
      const fresh = new DOMParser().parseFromString(html, 'text/html');
      const bag = fresh.querySelector('.header__bag');
      if (bag) header.querySelector('.header__bag')?.replaceWith(bag);
    }
    button.textContent = button.dataset.addedLabel;
    if (status) status.textContent = `${data.product_title || ''} ${button.dataset.addedLabel}`.trim();
  } catch (error) {
    button.disabled = false;
    const message = error.message || status?.dataset.error || '';
    let note = form.querySelector('.swatch-card__error');
    if (!note) {
      note = document.createElement('span');
      note.className = 'swatch-card__error';
      form.append(note);
    }
    note.textContent = message;
    if (status) status.textContent = message;
  }
});

document.addEventListener('DOMContentLoaded', () => {
  preventOrphans();

  // Submit collection sort forms on change
  document.querySelectorAll('form[data-auto-submit]').forEach((form) => {
    form.addEventListener('change', () => form.submit());
  });
});
